import * as React from "react";

import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { TagInput, Tags, TagsGroup } from "@/shared/components/tags";
import { SkillAccessibility, SkillSummary } from "@/shared/domain/skills";
import { useUpdateSkillMetadata } from "@/shared/domain/skills/hooks";

import { EditableSkillName } from "./EditableSkillName";
import styles from "./SkillHeader.module.css";
import { VisibilityChip } from "./VisibilityChip";
import { VisibilitySelect } from "./VisibilitySelect";

interface Props {
  summary: SkillSummary;
  /** Author, or a subscribed member of an `org-editable` skill. */
  canEdit: boolean;
  /** Accessibility is an ownership decision — author-only, even on org-editable. */
  isAuthor: boolean;
  orgId: string | undefined;
}

/**
 * The skill's identity: its name, who made it, who can see it, and its tags.
 *
 * Every field here edits *in place* and saves on its own — renaming the skill,
 * retagging it, and changing its accessibility are three independent actions,
 * none of which involves a form mode or a dialog. That is deliberately separate
 * from editing a *version*, which is a body of prose worth an explicit
 * edit/save cycle (`?edit=1` and the Save buttons below). Conflating the two
 * made it unclear which fields a Save was about to write.
 *
 * Tags go through `put_tags` / `remove_tags` one at a time, so two people
 * tagging the same skill concurrently merge rather than clobber.
 */
export function SkillHeader({ summary, canEdit, isAuthor, orgId }: Props) {
  const updateMetadata = useUpdateSkillMetadata(orgId);
  const [error, setError] = React.useState<string | null>(null);
  const skillId = summary.skill.skill_id;

  async function mutate(
    request: Parameters<typeof updateMetadata.mutateAsync>[0]["request"],
  ): Promise<void> {
    setError(null);
    await updateMetadata.mutateAsync({ skillId, request });
  }

  function changeAccessibility(next: SkillAccessibility) {
    if (next === summary.skill.accessibility) {
      return;
    }
    mutate({ accessibility: next }).catch((err: unknown) => {
      setError(
        err instanceof Error ? err.message : "Couldn't change accessibility.",
      );
    });
  }

  function addTag(tag: string) {
    mutate({ put_tags: [tag] }).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : "Couldn't add tag.");
    });
  }

  function removeTag(tag: string) {
    mutate({ remove_tags: [tag] }).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : "Couldn't remove tag.");
    });
  }

  return (
    <header className={styles.header}>
      <EditableSkillName
        name={summary.skill.name}
        canEdit={canEdit}
        onRename={(name) => mutate({ name })}
      />

      <div className={styles.facts}>
        <span className={styles.createdBy}>
          Created by <strong>{summary.skill.created_by}</strong>
        </span>
        <span className={styles.separator} aria-hidden="true">
          ·
        </span>
        {isAuthor ? (
          <VisibilitySelect
            accessibility={summary.skill.accessibility}
            isPending={updateMetadata.isPending}
            onChange={changeAccessibility}
          />
        ) : (
          <VisibilityChip accessibility={summary.skill.accessibility} />
        )}
      </div>

      {/* Tags own their line: a chip strip that grows sideways would otherwise
          push the accessibility control around as tags are added. */}
      {(canEdit || summary.skill.tags.length > 0) && (
        <TagsGroup>
          <Tags
            tags={summary.skill.tags}
            onDeleteTag={canEdit ? removeTag : undefined}
            truncateText={false}
          />
          {canEdit && (
            <TagInput
              autocompleteType={AutocompleteType.SkillTags}
              onAddTag={addTag}
              tags={summary.skill.tags}
              size="small"
              disabled={updateMetadata.isPending}
            />
          )}
        </TagsGroup>
      )}

      {error !== null && <small className={styles.error}>{error}</small>}
    </header>
  );
}
